<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { api } from '../api'
import { t } from '../i18n'

/**
 * "Which environment am I looking at?"
 *
 * The console shows treasury balances, the solvency verdict, and can force-verify
 * a customer's identity. Sandbox and production render identically, so without
 * this an operator with both open has nothing to tell them apart — and the
 * destructive actions are the ones you least want performed against the wrong
 * one.
 *
 * Removed on request in `3365c91` (2026-07-29, "Removes the environment banner
 * from all four clients") and reinstated on request 2026-08-07 — both decisions,
 * not accidents. `api.env()` sat here declared and uncalled in between, which is
 * the shape a deliberately-removed feature leaves behind: the seam survives, so
 * restoring it costs one component rather than a redesign.
 *
 * 🔴 IT FAILS LOUD, NOT SILENT. Three states, and the third is the point:
 *
 *   production   render nothing
 *   anything else render the environment's name
 *   UNREADABLE    render "environment unknown"
 *
 * Treating an unreadable answer as production is the one wrong move here: the
 * absence of a banner is what an operator reads as "this is live", so a failed
 * request must not be able to manufacture that. Same reasoning as the sweep
 * refusing to credit on a vendor's word, and `_card_pool()` refusing to count an
 * unreadable balance as zero.
 *
 * `state` starts null and renders nothing, so production never flashes a banner
 * while the request is in flight.
 */
const state = ref<{ env: string; known: boolean } | null>(null)

onMounted(async () => {
  try {
    const { env } = await api.env()
    state.value = { env, known: true }
  } catch {
    // Deliberately not surfaced as a toast. The banner IS the report, and an
    // operator who cannot reach the API has louder problems than this strip.
    state.value = { env: '', known: false }
  }
})

const visible = () => state.value !== null && state.value.env !== 'production'
</script>

<template>
  <div
    v-if="visible()"
    role="status"
    class="sticky top-0 z-50 flex items-center justify-center gap-2 bg-warn px-4 py-1
           text-center text-[12px] font-semibold uppercase tracking-wide text-ink"
  >
    <span aria-hidden="true">⚠</span>
    <span v-if="state?.known">{{ t('env.banner').replace('{env}', state.env) }}</span>
    <span v-else>{{ t('env.unknown') }}</span>
  </div>
</template>
